// Client side of api/contact.js (same setup as quickvents.com). The form gets a
// signed spam-protection token when the page loads and sends it back with the
// message; the function emails the lead through Gmail.
const FORM_ENDPOINT = '/api/contact';

// The API treats messages sent within 3 seconds of getting a token as bots.
const MIN_TOKEN_AGE_MS = 3200;
const MAX_TOKEN_AGE_MS = 12 * 60 * 60 * 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Formats US numbers as (561) 555-0100 while typing; 10 digits max.
export function formatPhone(value) {
    let d = value.replace(/\D/g, '');
    if (d[0] === '1') d = d.slice(1);
    d = d.slice(0, 10);
    if (d.length === 0) return '';
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export const isValidPhone = (value) => /^[2-9]\d{2}[2-9]\d{6}$/.test(value.replace(/\D/g, ''));

async function fetchToken() {
    const res = await fetch(FORM_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Token request failed: ${res.status}`);
    }
    const { token } = await res.json();
    return { token, issuedAt: Date.now() };
}

// One token per page load, refreshed when stale or after a failed send.
let tokenPromise = null;

export function prefetchToken() {
    if (!tokenPromise) tokenPromise = fetchToken().catch((err) => { tokenPromise = null; throw err; });
    return tokenPromise.catch(() => null);
}

// fields: { name, phone, email, message, service?, zip?, company? (honeypot) }
export async function sendContact(fields) {
    try {
        let t = await prefetchToken();
        // A failed prefetch is retried once here so its real error surfaces.
        if (!t || Date.now() - t.issuedAt > MAX_TOKEN_AGE_MS) {
            tokenPromise = null;
            t = await (tokenPromise = fetchToken());
        }
        const age = Date.now() - t.issuedAt;
        if (age < MIN_TOKEN_AGE_MS) await wait(MIN_TOKEN_AGE_MS - age);

        const res = await fetch(FORM_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ ...fields, token: t.token }),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Request failed: ${res.status}`);
        }
    } catch (err) {
        tokenPromise = null;
        throw err;
    }
}
